import {
  VILLAGE_KINDS,
  VILLAGE_PICK_KINDS,
  VILLAGE_PRINTABLE_FILES,
  type AgeBand,
  type VillageAdminItem,
  type VillageContent,
  type VillageGuideBlock,
  type VillageKind,
  type VillagePrintableFile,
} from '@kk/domain';
import { Card, Text, Title2, WasabiButton } from '@kk/ui';
import { useState } from 'react';

import { useContentCan } from '@/auth/SessionProvider';
import { LinkButton, Status } from '@/components/bits';
import { deck } from '@/copy/deck';
import { useClients } from '@/auth/SessionProvider';
import {
  useErrorMessage,
  useVillageItems,
  useVillageMutation,
  useVillagePartners,
  useVillageWeekPicks,
} from '@/data/hooks';

const c = deck.vsq;

/** A guide's text as the editor shows it: a blank line between paragraphs, "## " before a subhead. */
export const bodyToText = (body: readonly VillageGuideBlock[] | undefined): string =>
  (body ?? []).map((b) => (b.type === 'heading' ? `## ${b.text}` : b.text)).join('\n\n');
export const textToBody = (text: string): VillageGuideBlock[] =>
  text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) =>
      p.startsWith('## ')
        ? { type: 'heading', text: p.slice(3).trim() }
        : { type: 'paragraph', text: p },
    );

/** The editor's fields, as strings, and back into VillageContent (empty fields left out). */
type Form = {
  kind: VillageKind;
  title: string;
  line: string;
  provider: string;
  minAge: string;
  maxAge: string;
  readers: AgeBand[];
  readMinutes: string;
  picture: string;
  body: string;
  printable: string;
  startsAt: string;
  familyPlusOnly: boolean;
  linkUrl: string;
  linkLabel: string;
  partnerId: string;
};

const toLocal = (iso?: string) => (iso ? new Date(iso).toISOString().slice(0, 16) : '');

export function formFrom(content?: VillageContent): Form {
  return {
    kind: content?.kind ?? 'guide',
    title: content?.title ?? '',
    line: content?.line ?? '',
    provider: content?.provider ?? '',
    minAge: content?.audience ? String(content.audience.minAge) : '',
    maxAge: content?.audience ? String(content.audience.maxAge) : '',
    readers: content?.audience?.readingLevels ?? [],
    readMinutes: content?.readMinutes ? String(content.readMinutes) : '',
    picture: content?.picture ?? '',
    body: bodyToText(content?.body),
    printable: content?.printable ?? '',
    startsAt: toLocal(content?.event?.startsAt),
    familyPlusOnly: content?.event?.familyPlusOnly ?? false,
    linkUrl: content?.link?.url ?? '',
    linkLabel: content?.link?.label ?? '',
    partnerId: content?.partnerId ?? '',
  };
}

export function contentFrom(f: Form): VillageContent {
  const ages = f.minAge.trim() && f.maxAge.trim();
  return {
    kind: f.kind,
    title: f.title,
    ...(f.line.trim() ? { line: f.line } : {}),
    ...(f.provider.trim() ? { provider: f.provider.trim() } : {}),
    ...(ages
      ? {
          audience: {
            minAge: Number(f.minAge),
            maxAge: Number(f.maxAge),
            ...(f.readers.length ? { readingLevels: f.readers } : {}),
          },
        }
      : {}),
    ...((f.kind === 'guide' || f.kind === 'printable') && f.readMinutes.trim()
      ? { readMinutes: Number(f.readMinutes) }
      : {}),
    // P35's row picture (28 Sep): a guide's or printable's, an icon key from the library
    ...((f.kind === 'guide' || f.kind === 'printable') && f.picture.trim()
      ? { picture: f.picture.trim() }
      : {}),
    ...(f.kind === 'guide' ? { body: textToBody(f.body) } : {}),
    ...(f.kind === 'printable' && f.printable
      ? { printable: f.printable as VillagePrintableFile }
      : {}),
    ...(f.kind === 'event' && f.startsAt
      ? {
          event: {
            startsAt: new Date(f.startsAt).toISOString(),
            ...(f.familyPlusOnly ? { familyPlusOnly: true } : {}),
          },
        }
      : {}),
    ...(f.linkUrl.trim() ? { link: { url: f.linkUrl.trim(), label: f.linkLabel.trim() } } : {}),
    ...((f.kind === 'money' || f.kind === 'partnerOffer') && f.partnerId
      ? { partnerId: f.partnerId }
      : {}),
  };
}

/**
 * **VILLAGE SQUARE, THE ADMIN** (P35's notes): every item, drafts and live, with its totals; one editor for all six
 * kinds; the lessons' steps — approve the words, publish (someone other than the author, once two can publish), take
 * down, versions back to a draft. Partners' cards also need an owner to record the partner's approval of the words.
 */
export function VillageSquareScreen() {
  const items = useVillageItems();
  const errorMessage = useErrorMessage();
  const mayEdit = useContentCan('edit');
  const [editing, setEditing] = useState<{ id: string | null; form: Form } | null>(null);
  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <Title2 tone="ink">{c.title}</Title2>
          <Text variant="bodySm" tone="muted">
            {c.subtitle}
          </Text>
        </div>
        {mayEdit && !editing ? (
          <WasabiButton
            label={c.newItem}
            size="sm"
            fullWidth={false}
            onPress={() => setEditing({ id: null, form: formFrom() })}
            testID="vsq-new"
          />
        ) : null}
      </div>
      {editing ? <Editor editing={editing} onDone={() => setEditing(null)} /> : null}
      <WeeklyPicks items={items.data ?? []} mayEdit={mayEdit} />
      {items.error ? <Status testID="vsq-error">{errorMessage(items.error)}</Status> : null}
      <div className="stack">
        {(items.data ?? []).map((item) => (
          <ItemCard
            key={item.id}
            item={item}
            onEdit={
              mayEdit
                ? () => setEditing({ id: item.id, form: formFrom(item.draft ?? item.live) })
                : undefined
            }
          />
        ))}
      </div>
    </>
  );
}

function Editor({
  editing,
  onDone,
}: {
  editing: { id: string | null; form: Form };
  onDone: () => void;
}) {
  const { data } = useClients();
  const partners = useVillagePartners();
  const errorMessage = useErrorMessage();
  const [f, setF] = useState<Form>(editing.form);
  const save = useVillageMutation((content: VillageContent) =>
    editing.id ? data.updateVillageItem(editing.id, content) : data.createVillageItem(content),
  );
  const set =
    <K extends keyof Form>(k: K) =>
    (v: Form[K]) =>
      setF((old) => ({ ...old, [k]: v }));
  const input = (k: keyof Form, label: string, type = 'text') => (
    <label className="stack" key={k}>
      <span className="field-label">{label}</span>
      <input
        className="glyph-search"
        type={type}
        value={f[k] as string}
        onChange={(e) => set(k)(e.target.value as never)}
        data-testid={`vsq-field-${k}`}
      />
    </label>
  );
  return (
    <Card>
      <form
        className="stack"
        data-testid="vsq-editor"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(contentFrom(f), { onSuccess: onDone });
        }}
      >
        <span className="field-label">{c.fields.kind}</span>
        <div className="actions" role="radiogroup" aria-label={c.fields.kind}>
          {VILLAGE_KINDS.map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              className={`chip${f.kind === k ? ' on' : ''}`}
              aria-checked={f.kind === k}
              onClick={() => set('kind')(k)}
              data-testid={`vsq-kind-${k}`}
            >
              {c.kinds[k]}
            </button>
          ))}
        </div>
        {input('title', c.fields.title)}
        {input('line', c.fields.line)}
        {input('provider', c.fields.provider)}
        <div className="actions">
          {input('minAge', c.fields.minAge, 'number')}
          {input('maxAge', c.fields.maxAge, 'number')}
        </div>
        <span className="field-label">{c.fields.readers}</span>
        <div className="actions">
          {(['prereader', 'reader'] as const).map((r) => (
            <button
              key={r}
              type="button"
              className={`chip${f.readers.includes(r) ? ' on' : ''}`}
              aria-pressed={f.readers.includes(r)}
              onClick={() =>
                set('readers')(
                  f.readers.includes(r) ? f.readers.filter((x) => x !== r) : [...f.readers, r],
                )
              }
              data-testid={`vsq-reader-${r}`}
            >
              {c.readers[r]}
            </button>
          ))}
        </div>
        {f.kind === 'guide' ? (
          <>
            {input('readMinutes', c.fields.readMinutes, 'number')}
            <label className="stack">
              <span className="field-label">{c.fields.body}</span>
              <textarea
                className="script-text"
                value={f.body}
                onChange={(e) => set('body')(e.target.value)}
                data-testid="vsq-field-body"
              />
            </label>
          </>
        ) : null}
        {f.kind === 'guide' || f.kind === 'printable' ? input('picture', c.fields.picture) : null}
        {f.kind === 'printable' ? input('readMinutes', c.fields.printableMinutes, 'number') : null}
        {f.kind === 'printable' ? (
          <label className="stack">
            <span className="field-label">{c.fields.printable}</span>
            <select
              className="glyph-search"
              value={f.printable}
              onChange={(e) => set('printable')(e.target.value)}
              data-testid="vsq-field-printable"
            >
              <option value="" />
              {VILLAGE_PRINTABLE_FILES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {f.kind === 'event' ? (
          <>
            {input('startsAt', c.fields.startsAt, 'datetime-local')}
            <label className="actions">
              <input
                type="checkbox"
                checked={f.familyPlusOnly}
                onChange={(e) => set('familyPlusOnly')(e.target.checked)}
                data-testid="vsq-field-familyPlusOnly"
              />
              <span>{c.fields.familyPlusOnly}</span>
            </label>
          </>
        ) : null}
        {f.kind !== 'guide' && f.kind !== 'printable' ? (
          <>
            {input('linkUrl', c.fields.linkUrl)}
            {input('linkLabel', c.fields.linkLabel)}
          </>
        ) : null}
        {f.kind === 'money' || f.kind === 'partnerOffer' ? (
          <label className="stack">
            <span className="field-label">{c.fields.partner}</span>
            <select
              className="glyph-search"
              value={f.partnerId}
              onChange={(e) => set('partnerId')(e.target.value)}
              data-testid="vsq-field-partner"
            >
              <option value="">{c.noPartner}</option>
              {(partners.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {save.error ? <Status testID="vsq-save-error">{errorMessage(save.error)}</Status> : null}
        <div className="actions">
          <WasabiButton
            label={c.save}
            size="sm"
            fullWidth={false}
            onPress={() => save.mutate(contentFrom(f), { onSuccess: onDone })}
            testID="vsq-save"
          />
          <LinkButton label={c.cancel} onPress={onDone} testID="vsq-cancel" />
        </div>
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Card>
  );
}

function ItemCard({ item, onEdit }: { item: VillageAdminItem; onEdit?: () => void }) {
  const { data } = useClients();
  const errorMessage = useErrorMessage();
  const mayReview = useContentCan('review');
  const mayPublish = useContentCan('publish');
  const owner = useContentCan('team');
  const [showVersions, setShowVersions] = useState(false);
  const act = useVillageMutation(
    (action: 'review' | 'partner-approval' | 'publish' | 'take-down') =>
      data.villageItemAction(item.id, action),
  );
  const discard = useVillageMutation(() => data.discardVillageDraft(item.id));
  const content = item.draft ?? item.live;
  const id = item.id;
  return (
    <Card>
      <div className="stack" data-testid={`vsq-item-${id}`}>
        <div className="row-between">
          <Text variant="labelStrong">{content?.title ?? ''}</Text>
          <span className={`pill pill-${item.status}`} data-testid={`vsq-status-${id}`}>
            {c.status[item.status]}
            {item.draft && item.status !== 'draft' ? ` · ${c.status.draft}` : ''}
          </span>
        </div>
        <Text variant="bodySm" tone="muted">
          {`${content ? c.kinds[content.kind] : ''} · ${c.counts(item.counts.taps, item.counts.helpfulYes, item.counts.helpfulNo)}`}
        </Text>
        {item.draft ? (
          <Text variant="bodySm" tone="muted" testID={`vsq-review-${id}`}>
            {item.review ? (item.review.current ? c.reviewed : c.reviewStale) : ''}
          </Text>
        ) : null}
        <div className="actions">
          {onEdit ? <LinkButton label={c.edit} onPress={onEdit} testID={`vsq-edit-${id}`} /> : null}
          {item.draft && mayReview && !item.review?.current ? (
            <LinkButton
              label={c.review}
              onPress={() => act.mutate('review')}
              testID={`vsq-approve-words-${id}`}
            />
          ) : null}
          {item.draft?.partnerId && owner && !item.partnerApproval?.current ? (
            <LinkButton
              label={c.partnerCard}
              onPress={() => act.mutate('partner-approval')}
              testID={`vsq-partner-card-${id}`}
            />
          ) : null}
          {item.draft && mayPublish ? (
            <WasabiButton
              label={c.publish}
              size="sm"
              fullWidth={false}
              onPress={() => act.mutate('publish')}
              testID={`vsq-publish-${id}`}
            />
          ) : null}
          {item.status === 'live' && mayPublish ? (
            <LinkButton
              label={c.takeDown}
              onPress={() => act.mutate('take-down')}
              testID={`vsq-take-down-${id}`}
            />
          ) : null}
          {item.draft && item.liveVersion ? (
            <LinkButton
              label={c.discard}
              onPress={() => discard.mutate(undefined)}
              testID={`vsq-discard-${id}`}
            />
          ) : null}
          {item.liveVersion ? (
            <LinkButton
              label={c.versions}
              onPress={() => setShowVersions((v) => !v)}
              testID={`vsq-versions-${id}`}
            />
          ) : null}
        </div>
        {showVersions ? <Versions id={id} /> : null}
        {(act.error ?? discard.error) ? (
          <Status testID={`vsq-item-error-${id}`}>
            {errorMessage(act.error ?? discard.error)}
          </Status>
        ) : null}
      </div>
    </Card>
  );
}

function Versions({ id }: { id: string }) {
  const { data } = useClients();
  const [rows, setRows] = useState<Awaited<ReturnType<typeof data.villageItemVersions>> | null>(
    null,
  );
  const restore = useVillageMutation((version: number) =>
    data.restoreVillageItemVersion(id, version),
  );
  if (!rows) void data.villageItemVersions(id).then(setRows);
  return (
    <div className="stack" data-testid={`vsq-version-list-${id}`}>
      {(rows ?? []).map((v) => (
        <div key={v.version} className="row-between">
          <Text variant="bodySm">{`v${v.version} · ${new Date(v.publishedAt).toLocaleDateString()}`}</Text>
          <LinkButton
            label={c.restore}
            onPress={() => restore.mutate(v.version)}
            testID={`vsq-restore-${id}-${v.version}`}
          />
        </div>
      ))}
    </div>
  );
}

/** The Mondays of this week and the seven after, from the line-up's first week (the team's this week). */
function nextMondays(first: string, count = 8): string[] {
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(`${first}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 7 * i);
    return d.toISOString().slice(0, 10);
  });
}

/**
 * **P35d's WEEKLY PICK** (Evan, 28 Sep): the team lines up one guide or printable per week, the same for every family,
 * starting on each family's Monday. Each week shows what families will get as things stand — the pick while it is
 * live, else the newest guide.
 */
function WeeklyPicks({ items, mayEdit }: { items: VillageAdminItem[]; mayEdit: boolean }) {
  const { data } = useClients();
  const picks = useVillageWeekPicks();
  const errorMessage = useErrorMessage();
  const set = useVillageMutation((input: { week: string; itemId: string | null }) =>
    data.setVillageWeekPick(input.week, input.itemId),
  );
  const first = picks.data?.[0]?.week;
  if (!first)
    return picks.error ? (
      <Status testID="vsq-picks-error">{errorMessage(picks.error)}</Status>
    ) : null;
  const byWeek = new Map((picks.data ?? []).map((p) => [p.week, p]));
  const choices = items.filter((i) => {
    const content = i.live ?? i.draft;
    return (
      !!content &&
      VILLAGE_PICK_KINDS.includes(content.kind) &&
      !content.partnerId &&
      i.status !== 'takenDown'
    );
  });
  return (
    <Card>
      <div className="stack" data-testid="vsq-picks">
        <Title2 tone="ink">{c.picks.title}</Title2>
        <Text variant="bodySm" tone="muted">
          {c.picks.help}
        </Text>
        {nextMondays(first).map((week) => {
          const p = byWeek.get(week);
          return (
            <label className="stack" key={week}>
              <span className="field-label">{week}</span>
              <select
                className="glyph-search"
                value={p?.itemId ?? ''}
                disabled={!mayEdit || set.isPending}
                onChange={(e) => set.mutate({ week, itemId: e.target.value || null })}
                data-testid={`vsq-pick-${week}`}
              >
                <option value="">{c.picks.none}</option>
                {choices.map((i) => (
                  <option key={i.id} value={i.id}>
                    {(i.live ?? i.draft)!.title}
                  </option>
                ))}
              </select>
              {p ? (
                <Text variant="bodySm" tone="muted">
                  {p.served
                    ? p.served.fallback
                      ? c.picks.newest(p.served.title)
                      : c.picks.serves(p.served.title)
                    : c.picks.nothingLive}
                </Text>
              ) : null}
            </label>
          );
        })}
        {set.error ? <Status testID="vsq-pick-error">{errorMessage(set.error)}</Status> : null}
      </div>
    </Card>
  );
}
